import { faAppleAlt, faBookOpen, faCalendarAlt, faChartLine, faClock, faDollarSign, faFire, faLightbulb, faShoppingCart, faStar, faUser, faUsers, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllRecipes } from '../components/api';

function BudgetFriendly() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllRecipes()
      .then((all) => {
        const budgetRecipes = (all || []).filter((recipe) => recipe.budgetFriendly === true);
        
        const sorted = budgetRecipes.sort((a, b) => {
          const timeA = parseInt(a.cookingMinutes || 999);
          const timeB = parseInt(b.cookingMinutes || 999);
          return timeA - timeB;
        });

        setRecipes(sorted);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching budget recipes:', err);
        setLoading(false);
      });
  }, []);

  const handleRecipeClick = (id) => navigate(`/recipe/${id}`);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded w-64 mx-auto"></div>
            <div className="h-6 bg-gray-200 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background Image */}
      <div className="relative bg-gradient-to-r from-green-600 to-emerald-700 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/wat-eten-we-vandaag-studenten-maaltijden.jpg)`,
            opacity: 0.3
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-emerald-900/80"></div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
              <FontAwesomeIcon icon={faDollarSign} />
              Smart Cooking for Students
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg">
              Budget-Friendly Recipes
            </h1>
            <p className="text-xl sm:text-2xl text-green-50 max-w-3xl mx-auto drop-shadow-md">
              Delicious, nutritious meals that won't break the bank
            </p>
            <div className="flex flex-wrap justify-center gap-8 pt-4">
              <div className="text-center backdrop-blur-sm bg-white/10 px-6 py-4 rounded-lg">
                <div className="text-3xl font-bold">€15</div>
                <div className="text-green-100 text-sm">Max per person</div>
              </div>
              <div className="text-center backdrop-blur-sm bg-white/10 px-6 py-4 rounded-lg">
                <div className="text-3xl font-bold">{recipes.length}</div>
                <div className="text-green-100 text-sm">Recipes</div>
              </div>
              <div className="text-center backdrop-blur-sm bg-white/10 px-6 py-4 rounded-lg">
                <div className="text-3xl font-bold">&lt;60min</div>
                <div className="text-green-100 text-sm">Quick prep</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        
        {/* Student Health Message Section */}
        <section className="text-center space-y-6 max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900">Why Cooking at Home Matters for Students</h2>
          <div className="text-left bg-white p-8 rounded-2xl shadow-lg border-l-4 border-green-600">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              As a student, juggling classes, assignments, and social life can be overwhelming. After a long day at school, 
              it's tempting to grab fast food—it's quick, convenient, and doesn't require effort. But this habit comes at a cost: 
              <span className="font-bold text-gray-900"> your health, your wallet, and your energy levels.</span>
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              <span className="font-bold text-green-600">Staying healthy is crucial</span> during your studies. Your brain needs proper 
              nutrition to focus, retain information, and perform well in exams. Fast food might save time, but it often lacks the 
              essential nutrients your body needs and can leave you feeling sluggish and unmotivated.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The good news? <span className="font-bold text-gray-900">Cooking on a budget doesn't have to be complicated or time-consuming.</span> 
              With the right recipes, you can prepare delicious, nutritious meals in less time than it takes to order delivery—and 
              for a fraction of the cost. Plus, learning to cook is a life skill that will serve you well beyond your student years.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We understand the challenges you face. That's why we've curated these budget-friendly recipes specifically for students. 
              <span className="font-bold text-green-600"> Quick to make, easy on your wallet, and packed with the nutrition you need to thrive.</span> 
              Let's break the fast-food habit together and discover how simple it can be to eat well while studying.
            </p>
          </div>
        </section>

        {/* Why Budget-Friendly Section */}
        <section className="text-center space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Here Are Our Suggestions for Students</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These recipes are designed to fit your lifestyle—affordable, quick, and healthy meals that actually taste good.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faDollarSign} className="text-green-600 text-xl" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Cost-Effective</h3>
              <p className="text-sm text-gray-600">Maximum €15 per person without compromising on taste or nutrition</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faClock} className="text-blue-600 text-xl" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Time-Saving</h3>
              <p className="text-sm text-gray-600">Quick preparation under 60 minutes, perfect for busy student schedules</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faAppleAlt} className="text-purple-600 text-xl" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Nutritious</h3>
              <p className="text-sm text-gray-600">Balanced meals with essential nutrients to fuel your studies</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faUser} className="text-orange-600 text-xl" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Single-Serve</h3>
              <p className="text-sm text-gray-600">Perfectly portioned for 1-2 people, reducing food waste</p>
            </div>
          </div>
        </section>

        {/* Smart Shopping Tips */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-8 py-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <FontAwesomeIcon icon={faShoppingCart} />
              Smart Shopping Strategies
            </h2>
            <p className="text-green-50 mt-2">Essential tips to maximize your grocery budget</p>
          </div>
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faChartLine} className="text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Compare Prices</h3>
                    <p className="text-gray-600 text-sm">Check unit prices (price per kg/L) rather than package prices. Store brands often offer the same quality at 30-50% less cost.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Plan Weekly Menus</h3>
                    <p className="text-gray-600 text-sm">Create a meal plan before shopping to avoid impulse purchases. Use overlapping ingredients across recipes to reduce waste.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faFire} className="text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Buy Seasonal Produce</h3>
                    <p className="text-gray-600 text-sm">Seasonal fruits and vegetables are cheaper and fresher. Frozen alternatives are equally nutritious and last longer.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faBookOpen} className="text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Master Pantry Staples</h3>
                    <p className="text-gray-600 text-sm">Stock up on rice, pasta, canned beans, tomatoes, and spices. These versatile ingredients form the foundation of countless budget meals.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faUsers} className="text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Share & Split Costs</h3>
                    <p className="text-gray-600 text-sm">Coordinate with roommates to buy in bulk and share larger packages. Split cooking duties and meal costs.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faStar} className="text-yellow-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Batch Cook & Freeze</h3>
                    <p className="text-gray-600 text-sm">Cook larger portions and freeze individual servings. This saves time, money, and ensures you always have a meal ready.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pro Tips Section */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faLightbulb} className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pro Tips for Student Cooking</h2>
              <p className="text-gray-700">Expert advice to level up your budget cooking game</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">🥘 One-Pot Wonders</div>
              <p className="text-sm text-gray-600">Choose recipes that minimize dishes. One-pot meals save time on cooking and cleaning.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">♻️ Zero Waste Cooking</div>
              <p className="text-sm text-gray-600">Use vegetable scraps for stock, stale bread for croutons, and overripe fruit for smoothies.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">📦 Smart Storage</div>
              <p className="text-sm text-gray-600">Invest in airtight containers to keep ingredients fresh longer and reduce spoilage.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">🌿 Grow Herbs</div>
              <p className="text-sm text-gray-600">Keep a small herb garden on your windowsill. Fresh herbs elevate any dish affordably.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">⏰ Off-Peak Shopping</div>
              <p className="text-sm text-gray-600">Shop late evening for reduced-price items nearing their sell-by date but still perfectly safe.</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="font-semibold text-gray-900 mb-1">💪 Protein Hacks</div>
              <p className="text-sm text-gray-600">Eggs, lentils, chickpeas, and canned tuna offer affordable protein alternatives to meat.</p>
            </div>
          </div>
        </section>

        {/* Recipes Section */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Browse Our Budget-Friendly Recipes
            </h2>
            <p className="text-lg text-gray-600">
              {recipes.length} delicious recipes ready to save you money
            </p>
          </div>
          
          {recipes.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faUtensils} className="text-gray-400 text-2xl" />
              </div>
              <p className="text-gray-600 text-lg">No budget-friendly recipes found yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map((recipe) => (
                <div
                  key={recipe.id}
                  onClick={() => handleRecipeClick(recipe.id)}
                  className="group bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-green-300 transition-all duration-300 cursor-pointer overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {recipe.imageURL ? (
                      <img
                        src={recipe.imageURL}
                        alt={recipe.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FontAwesomeIcon icon={faUtensils} className="text-gray-400 text-5xl" />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      Budget-Friendly
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition">
                      {recipe.title}
                    </h3>
                    {recipe.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {recipe.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                      {recipe.cookingMinutes && (
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faClock} className="text-blue-500" />
                          <span>{recipe.cookingMinutes}min</span>
                        </div>
                      )}
                      {recipe.portionSize && (
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faUser} className="text-purple-500" />
                          <span>{recipe.portionSize} serving{recipe.portionSize > 1 ? 's' : ''}</span>
                        </div>
                      )}
                      {recipe.difficulty && (
                        <div className={`flex items-center gap-1.5 ${
                          recipe.difficulty.toLowerCase() === 'easy' ? 'text-green-600' :
                          recipe.difficulty.toLowerCase() === 'medium' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          <span className="font-medium">{recipe.difficulty}</span>
                        </div>
                      )}
                    </div>
                    {(recipe.vegan || recipe.highProtein || recipe.calories) && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                        {recipe.vegan && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                            🌱 Vegan
                          </span>
                        )}
                        {recipe.highProtein && (
                          <span className="px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded-full font-medium">
                            💪 High-Protein
                          </span>
                        )}
                        {recipe.calories && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                            {recipe.calories} kcal
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default BudgetFriendly;
